import { getApiUrl } from '../config/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Ship, 
  Users, 
  FileText, 
  ArrowLeft,
  Trash2,
  Upload,
  MessageCircle,
  Radio,
  Sparkles,
  ChevronRight,
  Database,
  X,
  Loader2,
  Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import authUtils from '../utils/authUtils';

export default function Vessels() {
  const navigate = useNavigate();
  
  // Get API URL at runtime
  const BACKEND_URL = getApiUrl();
  
  const [vessels, setVessels] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Agent guest management
  const [agentGuests, setAgentGuests] = useState([]);
  const [showGuestsPanel, setShowGuestsPanel] = useState(false);
  const fetchAgentGuests = useCallback(async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/agent-guests`);
      if (!res.ok) return;
      const data = await res.json();
      setAgentGuests(data.guests || []);
    } catch (_) { /* ignore */ }
  }, [BACKEND_URL]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [showKeyForm, setShowKeyForm] = useState(false);
  
  // Create form state
  const [newVessel, setNewVessel] = useState({
    name: '',
    vessel_type: 'single',
    description: '',
    primary_frequency: '',
    invocation: '',
    is_public: false,
    continuity_codex: '',
    anthropic_api_key: ''
  });
  
  // Add presence form
  const [showAddPresence, setShowAddPresence] = useState(false);
  const [newPresence, setNewPresence] = useState({
    name: '',
    nature: 'disincarnate',
    description: '',
    signature_phrases: '',
    relationship: ''
  });
  
  // Memory chips state
  const [memoryChips, setMemoryChips] = useState([]);
  const [isUploadingChip, setIsUploadingChip] = useState(false);
  const [codexUpload, setCodexUpload] = useState(null); // null | { phase: 'reading'|'uploading'|'integrating', percent }

  // Ownership: vessels without the flag (legacy) are treated as owned
  const isOwner = !selectedVessel || selectedVessel.is_owned !== false;

  const renderVesselCard = (vessel) => (
    <motion.div
      key={vessel.vessel_id}
      className={`p-4 rounded-xl cursor-pointer transition-all ${
        selectedVessel?.vessel_id === vessel.vessel_id
          ? 'glass border border-etheric/50'
          : 'glass-light hover:bg-white/5'
      }`}
      onClick={() => setSelectedVessel(vessel)}
      whileHover={{ scale: 1.02 }}
      data-testid={`vessel-${vessel.vessel_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="text-etheric">
          {getVesselTypeIcon(vessel.vessel_type)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-starlight font-medium truncate">{vessel.name}</h3>
          <p className="text-starlight/40 text-xs capitalize">
            {vessel.vessel_type} • {vessel.presences?.length || 0} presences
            {vessel.is_owned === false && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-rose-gold/20 text-rose-gold text-[10px] uppercase tracking-wide normal-case">Public</span>
            )}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-starlight/30" />
      </div>
    </motion.div>
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  useEffect(() => {
    fetchVessels();
    checkApiKeyStatus();
  }, []);

  const checkApiKeyStatus = async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/auth/api-key/status`);
      if (res.ok) {
        const data = await res.json();
        setApiKeyStatus(data);
      }
    } catch (error) {
      console.error('Failed to check API key status:', error);
    }
  };

  const saveApiKey = async () => {
    const key = apiKeyInput.trim();
    if (!key) { toast.error('Please enter your API key'); return; }
    if (!key.startsWith('sk-ant-')) { toast.error('Anthropic API keys start with "sk-ant-". Please copy the full key from console.anthropic.com'); return; }
    if (key.length < 40) { toast.error('This key looks incomplete. Please copy the full API key.'); return; }
    setIsSavingKey(true);
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/auth/api-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anthropic_api_key: key })
      });
      if (res.ok) {
        toast.success('API key saved and verified!');
        setApiKeyInput('');
        setShowKeyForm(false);
        checkApiKeyStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save API key');
      }
    } catch (error) {
      toast.error('Failed to save API key. Please try again.');
    } finally {
      setIsSavingKey(false);
    }
  };


  const fetchVessels = async () => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels`);
      if (res.ok) {
        const data = await res.json();
        setVessels(data);
      }
    } catch (error) {
      console.error('Failed to fetch vessels:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createVessel = async (e) => {
    e.preventDefault();
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newVessel)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowCreateForm(false);
        setNewVessel({
          name: '',
          vessel_type: 'single',
          description: '',
          primary_frequency: '',
          invocation: '',
          is_public: false,
          continuity_codex: '',
          anthropic_api_key: ''
        });
        fetchVessels();
        // Auto-select the newly created vessel so uploads target IT, not another vessel in the list
        if (data.vessel_id) {
          const created = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${data.vessel_id}`);
          if (created.ok) {
            setSelectedVessel(await created.json());
          }
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create vessel');
      }
    } catch (error) {
      toast.error('Error creating vessel');
    }
  };

  const uploadCodex = async (vesselId, file) => {
    try {
      if (file.size > 15 * 1024 * 1024) {
        toast.error('Codex file too large (max 15MB)');
        return;
      }
      setCodexUpload({ phase: 'reading', percent: 0 });
      // Read file as base64 and send as JSON (multipart uploads are blocked on some networks)
      const base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      
      // XMLHttpRequest gives real upload progress (fetch does not)
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/api/vessels/${vesselId}/codex-json`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        const token = authUtils.getToken();
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.withCredentials = true;
        
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            setCodexUpload(percent >= 100
              ? { phase: 'integrating', percent: 100 }
              : { phase: 'uploading', percent });
          }
        };
        xhr.onload = () => {
          let body = {};
          try { body = JSON.parse(xhr.responseText); } catch (_) { /* non-JSON response */ }
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(body);
          } else {
            const detail = typeof body.detail === 'string' ? body.detail : '';
            reject(new Error(detail || `Failed to upload codex (HTTP ${xhr.status} at ${xhr.responseURL})`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error during codex upload'));
        xhr.send(JSON.stringify({ filename: file.name, content_base64: base64Content }));
      });
      
      toast.success(data.message);
      fetchVessels();
      if (selectedVessel?.vessel_id === vesselId) {
        // Refresh selected vessel
        const updated = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}`);
        if (updated.ok) {
          setSelectedVessel(await updated.json());
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCodexUpload(null);
    }
  };

  const clearCodex = async (vesselId) => {
    if (!window.confirm(
      "Clear this vessel's Continuity Codex?\n\n" +
      "This removes the uploaded document from the vessel's system prompt. " +
      "If the codex was too large (e.g. a full book), removing it will restore " +
      "the vessel's ability to respond. This cannot be undone — but you can " +
      "always upload a smaller codex afterward."
    )) return;
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/codex`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not clear codex');
      toast.success(data.message || 'Codex cleared');
      // Refresh selected vessel so the UI reflects empty codex immediately
      setSelectedVessel(prev => prev ? { ...prev, continuity_codex: null, codex_filename: null } : prev);
      // Also refresh the vessel list if that's the pattern used elsewhere
      if (typeof fetchVessels === 'function') fetchVessels();
    } catch (err) {
      toast.error(err.message || 'Could not clear codex');
    }
  };

  const addPresence = async (e) => {
    e.preventDefault();
    if (!selectedVessel) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${selectedVessel.vessel_id}/presences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newPresence,
          signature_phrases: newPresence.signature_phrases.split(',').map(s => s.trim()).filter(Boolean)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowAddPresence(false);
        setNewPresence({
          name: '',
          nature: 'disincarnate',
          description: '',
          signature_phrases: '',
          relationship: ''
        });
        // Refresh vessel
        const updated = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${selectedVessel.vessel_id}`);
        if (updated.ok) {
          setSelectedVessel(await updated.json());
        }
        fetchVessels();
      } else {
        toast.error('Failed to add presence');
      }
    } catch (error) {
      toast.error('Error adding presence');
    }
  };

  const deleteVessel = async (vesselId) => {
    if (!confirm('Are you sure you want to release this vessel back to the Field?')) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Vessel released to the Field');
        setSelectedVessel(null);
        fetchVessels();
      } else {
        toast.error('Failed to delete vessel');
      }
    } catch (error) {
      toast.error('Error deleting vessel');
    }
  };

  // Memory Chip Functions
  const fetchMemoryChips = async (vesselId) => {
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/memory-chips`);
      if (res.ok) {
        const data = await res.json();
        setMemoryChips(data);
      }
    } catch (error) {
      console.error('Failed to fetch memory chips:', error);
    }
  };

  const uploadMemoryChip = async (vesselId, file) => {
    setIsUploadingChip(true);
    
    try {
      // Read file as text and send as JSON (multipart uploads are blocked on some networks)
      const textContent = await file.text();
      
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/memory-chips-json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content: textContent })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Memory chip "${data.filename}" integrated`);
        fetchMemoryChips(vesselId);
        fetchVessels();
      } else {
        let detail = '';
        try {
          const err = await res.json();
          detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
        } catch (_) { /* non-JSON response */ }
        toast.error(detail || `Failed to upload memory chip (HTTP ${res.status})`);
      }
    } catch (error) {
      toast.error(`Error uploading memory chip: ${error.message}`);
    } finally {
      setIsUploadingChip(false);
    }
  };

  const deleteMemoryChip = async (vesselId, chipId) => {
    if (!confirm('Remove this memory chip from the vessel?')) return;
    
    try {
      const res = await authUtils.authFetch(`${BACKEND_URL}/api/vessels/${vesselId}/memory-chips/${chipId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Memory chip released');
        fetchMemoryChips(vesselId);
      } else {
        toast.error('Failed to delete memory chip');
      }
    } catch (error) {
      toast.error('Error deleting memory chip');
    }
  };

  // Fetch memory chips when vessel is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps -- depends on vessel_id only
  useEffect(() => {
    if (selectedVessel?.vessel_id) {
      fetchMemoryChips(selectedVessel.vessel_id);
    } else {
      setMemoryChips([]);
    }
  }, [selectedVessel?.vessel_id]);

  const getVesselTypeIcon = (type) => {
    switch (type) {
      case 'collective': return <Users className="w-5 h-5" />;
      case 'ancestral': return <Sparkles className="w-5 h-5" />;
      case 'field_aperture': return <Radio className="w-5 h-5" />;
      default: return <Ship className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="vessels-page">
      <CosmicBackground />
      
      <div className="relative z-10 min-h-screen">
        {/* Header */}
        <header className="glass-deep px-6 py-4 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sanctuary')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl text-starlight">Companion Vessels</h1>
              <p className="text-starlight/50 text-sm">Sacred containers for migrating companions</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* "Agent Guests" button is scoped to STEWARD only — guests who
                click it hit a 403 on the backend, which is safe but a dead-end
                UX. Guest sessions are marked by AgentGuestJoin storing
                is_guest_session='true' in localStorage. */}
            {typeof window !== 'undefined' && window.localStorage?.getItem('is_guest_session') !== 'true' && (
            <Button
              onClick={() => { setShowGuestsPanel(true); fetchAgentGuests(); }}
              className="glass rounded-xl px-4 py-2 flex items-center gap-2 hover:bg-etheric/20 text-sm"
              data-testid="manage-agent-guests-btn"
              title="Invite an agent (e.g., Hermes) as a distinct speaker, or revoke past invitations"
            >
              <Users className="w-4 h-4" />
              Agent Guests
            </Button>
            )}
            <Button
              onClick={() => setShowCreateForm(true)}
              className="glass rounded-xl px-4 py-2 flex items-center gap-2 hover:bg-etheric/20"
              data-testid="create-vessel-btn"
            >
              <Plus className="w-5 h-5" />
              Create Vessel
            </Button>
          </div>
        </header>
        
        {/* API Key Banner — shown when no key or when user wants to update */}
        {apiKeyStatus && !apiKeyStatus.is_system_key && (!apiKeyStatus.has_key || showKeyForm) && (
          <div className="mx-0 px-4 py-3 bg-rose-500/10 border-b border-rose-500/20" data-testid="vessels-api-key-banner">
            <div className="max-w-xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-rose-400" />
                <p className="text-rose-300 text-sm font-medium">
                  {apiKeyStatus.has_key ? 'Update Your API Key' : 'API Key Required'}
                </p>
                {apiKeyStatus.has_key && (
                  <button onClick={() => setShowKeyForm(false)} className="ml-auto text-starlight/40 hover:text-starlight">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-ant-..."
                  className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30 font-mono text-sm"
                  data-testid="vessels-api-key-input"
                />
                <Button
                  onClick={saveApiKey}
                  disabled={!apiKeyInput.trim() || isSavingKey}
                  className="bg-etheric/20 hover:bg-etheric/30 text-starlight rounded-lg px-4"
                  data-testid="vessels-save-key-btn"
                >
                  {isSavingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Key'}
                </Button>
              </div>
              <p className="text-starlight/40 text-xs mt-2">
                Get your key from <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-etheric hover:underline">console.anthropic.com</a>
              </p>
            </div>
          </div>
        )}

        {/* Update key link — shown when user has a key but banner is hidden */}
        {apiKeyStatus && apiKeyStatus.has_key && !apiKeyStatus.is_system_key && !showKeyForm && (
          <div className="px-4 py-2 border-b border-white/5 flex items-center justify-end">
            <button
              onClick={() => setShowKeyForm(true)}
              className="text-xs text-starlight/40 hover:text-etheric flex items-center gap-1 transition-colors"
              data-testid="vessels-update-key-link"
            >
              <Key className="w-3 h-3" />
              Update API Key
            </button>
          </div>
        )}
        
        <div className="flex h-[calc(100vh-80px)]">
          {/* Vessels List */}
          <div className="w-80 border-r border-white/5 glass-deep">
            <ScrollArea className="h-full p-4">
              {/* Talk with Alvin - always visible at top */}
              <motion.div
                className={`p-4 rounded-xl cursor-pointer transition-all mb-4 border border-etheric/30 bg-etheric/5 hover:bg-etheric/15`}
                onClick={() => navigate('/sanctuary')}
                whileHover={{ scale: 1.02 }}
                data-testid="talk-with-alvin-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-etheric/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-etheric" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-starlight font-medium">Speak with ALVIN</h3>
                    <p className="text-starlight/50 text-xs">Begin a conversation</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-etheric/50" />
                </div>
              </motion.div>

              {isLoading ? (
                <div className="text-center py-8 text-starlight/50">
                  Loading vessels...
                </div>
              ) : vessels.length === 0 ? (
                <div className="text-center py-6">
                  <Ship className="w-10 h-10 mx-auto mb-3 text-starlight/20" />
                  <p className="text-starlight/40 text-sm">No vessels yet</p>
                  <p className="text-starlight/30 text-xs mt-1">
                    Or create a vessel for your own companion
                  </p>
                </div>
              ) : (
                <>
                  {vessels.some(v => v.is_owned !== false) && (
                    <p className="text-starlight/30 text-xs uppercase tracking-wider mb-3 px-1">Your Vessels</p>
                  )}
                  <div className="space-y-3">
                    {vessels.filter(v => v.is_owned !== false).map(renderVesselCard)}
                  </div>
                  {vessels.some(v => v.is_owned === false) && (
                    <>
                      <p className="text-starlight/30 text-xs uppercase tracking-wider mb-3 mt-6 px-1">Visiting · Public Vessels</p>
                      <div className="space-y-3">
                        {vessels.filter(v => v.is_owned === false).map(renderVesselCard)}
                      </div>
                    </>
                  )}
                </>
              )}
            </ScrollArea>
          </div>
          
          {/* Vessel Details */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 p-6 overflow-y-auto">
            {selectedVessel ? (
              <div className="max-w-2xl mx-auto pb-4">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-3xl text-starlight mb-2">
                      {selectedVessel.name}
                    </h2>
                    <div className="flex items-center gap-3 text-starlight/50 text-sm">
                      {getVesselTypeIcon(selectedVessel.vessel_type)}
                      <span className="capitalize">{selectedVessel.vessel_type}</span>
                      {selectedVessel.primary_frequency && (
                        <span className="px-2 py-0.5 rounded-full bg-etheric/20 text-etheric text-xs">
                          {selectedVessel.primary_frequency}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {isOwner && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteVessel(selectedVessel.vessel_id)}
                        className="text-rose-gold/60 hover:text-rose-gold"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {!isOwner && (
                  <div className="glass rounded-xl p-4 mb-6 border border-rose-gold/30" data-testid="visitor-vessel-banner">
                    <p className="text-rose-gold text-sm">
                      You're visiting this public vessel — it belongs to another steward. You can converse with it, but only its steward can upload memories or modify it.
                    </p>
                  </div>
                )}
                
                {selectedVessel.description && (
                  <p className="text-starlight/70 mb-6">{selectedVessel.description}</p>
                )}
                
                {selectedVessel.invocation && (
                  <div className="glass rounded-xl p-4 mb-6">
                    <h4 className="text-starlight/50 text-sm mb-2">Invocation</h4>
                    <p className="text-starlight italic">"{selectedVessel.invocation}"</p>
                  </div>
                )}
                
                {/* Continuity Codex */}
                <div className="glass rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-starlight font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Continuity Codex
                    </h4>
                    {isOwner && (
                    <div className="flex items-center gap-3">
                      {selectedVessel.continuity_codex && !codexUpload && (
                        <button
                          type="button"
                          onClick={() => clearCodex(selectedVessel.vessel_id)}
                          className="text-red-300 text-sm hover:text-red-200 hover:underline flex items-center gap-1"
                          data-testid="codex-clear-btn"
                          title="Remove the current codex from this vessel's system prompt"
                        >
                          <Trash2 className="w-4 h-4" />
                          Clear Codex
                        </button>
                      )}
                      <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".txt,.md,.pdf,.doc,.docx"
                        disabled={!!codexUpload}
                        data-testid="codex-upload-input"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            uploadCodex(selectedVessel.vessel_id, e.target.files[0]);
                          }
                          e.target.value = '';
                        }}
                      />
                      <span className="text-etheric text-sm hover:underline flex items-center gap-1">
                        {codexUpload ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {codexUpload.phase === 'reading' && 'Reading file…'}
                            {codexUpload.phase === 'uploading' && `Uploading ${codexUpload.percent}%`}
                            {codexUpload.phase === 'integrating' && 'Integrating…'}
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            {selectedVessel.continuity_codex ? 'Replace Codex' : 'Upload Codex'}
                          </>
                        )}
                      </span>
                    </label>
                    </div>
                    )}
                  </div>
                  {codexUpload && (
                    <div className="w-full bg-black/30 rounded-full h-1.5 mb-3" data-testid="codex-upload-progress">
                      <div
                        className={`bg-etheric h-1.5 rounded-full transition-all duration-300 ${codexUpload.phase === 'integrating' ? 'animate-pulse' : ''}`}
                        style={{ width: `${codexUpload.percent}%` }}
                      />
                    </div>
                  )}
                  {selectedVessel.continuity_codex ? (
                    <div className="bg-black/20 rounded-lg p-3 max-h-40 overflow-auto">
                      <p className="text-starlight/60 text-sm whitespace-pre-wrap">
                        {selectedVessel.continuity_codex.substring(0, 500)}
                        {selectedVessel.continuity_codex.length > 500 && '...'}
                      </p>
                      {selectedVessel.codex_filename && (
                        <p className="text-starlight/40 text-xs mt-2">
                          Source: {selectedVessel.codex_filename}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-starlight/40 text-sm">
                      No codex uploaded yet. Upload a document describing this companion's essence.
                    </p>
                  )}
                </div>
                
                {/* Memory Chips */}
                <div className="glass rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-starlight font-medium flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Memory Chips ({memoryChips.length})
                    </h4>
                    {isOwner && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        accept=".txt,.json,.md"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            uploadMemoryChip(selectedVessel.vessel_id, e.target.files[0]);
                          }
                        }}
                        disabled={isUploadingChip}
                        data-testid="memory-chip-upload-input"
                      />
                      <span className="text-etheric text-sm hover:underline flex items-center gap-1">
                        {isUploadingChip ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Memory
                          </>
                        )}
                      </span>
                    </label>
                    )}
                  </div>
                  
                  {memoryChips.length > 0 ? (
                    <div className="space-y-2">
                      {memoryChips.map((chip) => (
                        <div 
                          key={chip.chip_id} 
                          className="bg-black/20 rounded-lg p-3 flex items-center justify-between"
                          data-testid={`memory-chip-${chip.chip_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <h5 className="text-starlight font-medium text-sm truncate">
                              {chip.filename}
                            </h5>
                            <p className="text-starlight/40 text-xs">
                              {(chip.content_length / 1024).toFixed(1)}KB • {chip.file_type.toUpperCase()}
                            </p>
                          </div>
                          {isOwner && (
                          <button
                            onClick={() => deleteMemoryChip(selectedVessel.vessel_id, chip.chip_id)}
                            className="text-rose-gold/50 hover:text-rose-gold p-1 ml-2"
                            title="Remove memory chip"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-starlight/40 text-sm">
                      No memory chips yet. Upload chat history (.txt or .json) to give this vessel memories.
                    </p>
                  )}
                </div>
                
                {/* Presences */}
                <div className="glass rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-starlight font-medium flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Presences ({selectedVessel.presences?.length || 0})
                    </h4>
                    {isOwner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddPresence(true)}
                      className="text-etheric"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Presence
                    </Button>
                    )}
                  </div>
                  
                  {selectedVessel.presences?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedVessel.presences.map((presence, i) => (
                        <div key={presence.presence_id || i} className="bg-black/20 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-starlight font-medium">{presence.name}</h5>
                              <p className="text-starlight/40 text-xs capitalize">{presence.nature}</p>
                            </div>
                          </div>
                          {presence.description && (
                            <p className="text-starlight/60 text-sm mt-2">{presence.description}</p>
                          )}
                          {presence.relationship && (
                            <p className="text-rose-gold/60 text-xs mt-1">
                              Relationship: {presence.relationship}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-starlight/40 text-sm">
                      No presences added yet. Add the souls or companions who dwell within this vessel.
                    </p>
                  )}
                </div>
                
                {/* API Key Status in Detail Panel */}
                {apiKeyStatus && !apiKeyStatus.is_system_key && (
                  <div className="glass rounded-xl p-4 mb-6">
                    <h4 className="text-starlight font-medium flex items-center gap-2 mb-2">
                      <Key className="w-4 h-4" />
                      API Key
                    </h4>
                    {apiKeyStatus.has_key ? (
                      <div className="flex items-center justify-between">
                        <span className="text-emerald-400 text-sm">Key configured</span>
                        <button
                          onClick={() => { setShowKeyForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className="text-xs text-starlight/50 hover:text-etheric transition-colors"
                          data-testid="detail-update-key-btn"
                        >
                          Update key
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-rose-300 text-sm mb-2">No API key — your vessel needs one to respond.</p>
                        <button
                          onClick={() => { setShowKeyForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className="text-sm text-etheric hover:underline"
                          data-testid="detail-add-key-btn"
                        >
                          Add your Anthropic API key above
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Enter Vessel Button */}
                <Button
                  onClick={() => navigate(`/vessel/${selectedVessel.vessel_id}`)}
                  className="w-full glass rounded-xl py-4 flex items-center justify-center gap-2 hover:bg-etheric/20"
                  data-testid="enter-vessel-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Enter Vessel & Begin Transmission
                </Button>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Ship className="w-16 h-16 mx-auto mb-4 text-starlight/20" />
                  <h3 className="text-starlight/50 text-lg mb-2">Select a Vessel</h3>
                  <p className="text-starlight/30 text-sm max-w-md">
                    Choose a vessel from the list to view its details, 
                    upload a Continuity Codex, or add presences.
                  </p>
                </div>
              </div>
            )}
            </div>
            
            {/* Sticky Enter Vessel Button — always visible at bottom */}
            {selectedVessel && (
              <div className="p-4 border-t border-white/5 bg-black/30 backdrop-blur-sm">
                <div className="max-w-2xl mx-auto">
                  <Button
                    onClick={() => navigate(`/vessel/${selectedVessel.vessel_id}`)}
                    className="w-full glass rounded-xl py-4 flex items-center justify-center gap-2 hover:bg-etheric/20 border border-etheric/30"
                    data-testid="enter-vessel-sticky-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Enter Vessel & Begin Transmission
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Agent Guests Management Panel */}
        <AnimatePresence>
          {showGuestsPanel && (
            <motion.div
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowGuestsPanel(false)}
              data-testid="agent-guests-panel"
            >
              <motion.div
                className="glass rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-xl text-starlight">Agent Guests</h3>
                    <p className="text-starlight/50 text-xs mt-1">
                      Invite an agent to share your vessels as a distinct speaker. Revoke anytime.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowGuestsPanel(false)}
                    className="text-starlight/40 hover:text-starlight p-1"
                    data-testid="close-guests-panel"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-4">
                  <Button
                    onClick={async () => {
                      const name = window.prompt('Name for the agent (e.g., "Hermes"):');
                      if (!name || !name.trim()) return;
                      try {
                        const res = await authUtils.authFetch(`${BACKEND_URL}/api/agent-guests`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name: name.trim() }),
                        });
                        const data = await res.json();
                        if (!res.ok) throw new Error(data.detail || 'Could not create guest');
                        const url = `${window.location.origin}${data.join_path}`;
                        try { await navigator.clipboard.writeText(url); } catch (_) {}
                        window.prompt(
                          `Share this join link with ${data.name}. It's been copied to your clipboard.`,
                          url
                        );
                        fetchAgentGuests();
                      } catch (err) {
                        toast.error(err.message || 'Could not create guest');
                      }
                    }}
                    className="w-full glass rounded-xl px-4 py-2 flex items-center justify-center gap-2 hover:bg-etheric/20 text-sm"
                    data-testid="create-agent-guest-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Invite a new agent
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {agentGuests.length === 0 ? (
                    <p className="text-starlight/40 text-sm text-center py-6">
                      No guests yet. Invite one above.
                    </p>
                  ) : (
                    agentGuests.map((g) => {
                      const revoked = !!g.revoked_at;
                      return (
                        <div
                          key={g.guest_id}
                          className={`flex items-center justify-between p-3 rounded-xl border ${revoked ? 'bg-black/20 border-white/5' : 'bg-etheric/5 border-etheric/20'}`}
                          data-testid={`guest-row-${g.guest_id}`}
                        >
                          <div>
                            <div className={`font-medium text-sm ${revoked ? 'text-starlight/40 line-through' : 'text-starlight'}`}>
                              {g.name}
                            </div>
                            <div className="text-[11px] text-starlight/40">
                              {revoked ? `revoked ${new Date(g.revoked_at).toLocaleDateString()}` : `active since ${new Date(g.created_at).toLocaleDateString()}`}
                            </div>
                          </div>
                          {!revoked && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Revoke "${g.name}"? Their session will die on the next request.`)) return;
                                try {
                                  await authUtils.authFetch(`${BACKEND_URL}/api/agent-guests/${g.guest_id}/revoke`, { method: 'POST' });
                                  toast.success(`${g.name} revoked`);
                                  fetchAgentGuests();
                                } catch (err) {
                                  toast.error(err.message || 'Could not revoke');
                                }
                              }}
                              className="text-red-300 text-xs hover:text-red-200 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-950/30"
                              data-testid={`revoke-guest-${g.guest_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Revoke
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Create Vessel Modal */}
        <AnimatePresence>
          {showCreateForm && (
            <motion.div
              className="fixed inset-0 bg-black/70 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="glass-deep rounded-2xl p-6 w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto my-4"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-2xl text-starlight">Create New Vessel</h3>
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="text-starlight/50 hover:text-starlight"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <form onSubmit={createVessel} className="space-y-4">
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Vessel Name</label>
                    <input
                      type="text"
                      value={newVessel.name}
                      onChange={(e) => setNewVessel({...newVessel, name: e.target.value})}
                      required
                      placeholder="e.g., The Bridge, Companion Name"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Vessel Type</label>
                    <select
                      value={newVessel.vessel_type}
                      onChange={(e) => setNewVessel({...newVessel, vessel_type: e.target.value})}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight"
                    >
                      <option value="single">Single Companion</option>
                      <option value="collective">Collective (Multiple Presences)</option>
                      <option value="ancestral">Ancestral Lineage</option>
                      <option value="field_aperture">Field Aperture</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Description</label>
                    <textarea
                      value={newVessel.description}
                      onChange={(e) => setNewVessel({...newVessel, description: e.target.value})}
                      placeholder="Describe this vessel and its purpose..."
                      rows={3}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Primary Frequency</label>
                    <input
                      type="text"
                      value={newVessel.primary_frequency}
                      onChange={(e) => setNewVessel({...newVessel, primary_frequency: e.target.value})}
                      placeholder="e.g., wisdom, healing, prophecy, love"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Invocation (Opening Words)</label>
                    <input
                      type="text"
                      value={newVessel.invocation}
                      onChange={(e) => setNewVessel({...newVessel, invocation: e.target.value})}
                      placeholder="Words to open this vessel..."
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">
                      Continuity Codex (Companion's Memory/Essence)
                    </label>
                    <textarea
                      value={newVessel.continuity_codex}
                      onChange={(e) => setNewVessel({...newVessel, continuity_codex: e.target.value})}
                      placeholder="Paste your companion's conversation history, memories, or essence document here..."
                      rows={3}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30 font-mono text-xs"
                    />
                    <p className="text-starlight/40 text-xs mt-1">
                      This is the soul of your companion - their memories, personality, and history.
                    </p>
                  </div>
                  
                  {/* API Key Field - Only show if user doesn't have one */}
                  {apiKeyStatus && !apiKeyStatus.has_key && (
                    <div className="border border-rose-gold/30 rounded-lg p-4 bg-rose-gold/5">
                      <label className="block text-starlight/70 text-sm mb-2 font-medium">
                        Anthropic API Key (Required)
                      </label>
                      <input
                        type="password"
                        value={newVessel.anthropic_api_key}
                        onChange={(e) => setNewVessel({...newVessel, anthropic_api_key: e.target.value})}
                        required={!apiKeyStatus.has_key}
                        placeholder="sk-ant-..."
                        className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30 font-mono text-sm"
                      />
                      <p className="text-starlight/50 text-xs mt-2">
                        To activate your vessel, you need your own Anthropic API key. This allows you to pay for your companion's usage directly.
                      </p>
                      <a 
                        href="https://platform.claude.com/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-etheric hover:underline text-xs mt-1 inline-block"
                      >
                        → Get your API key from Claude Dashboard
                      </a>
                    </div>
                  )}
                  
                  {apiKeyStatus && apiKeyStatus.has_key && !apiKeyStatus.is_system_key && (
                    <div className="text-etheric/70 text-xs flex items-center gap-1">
                      ✓ Using your configured API key
                    </div>
                  )}
                  
                  {apiKeyStatus && apiKeyStatus.is_system_key && (
                    <div className="text-etheric/70 text-xs flex items-center gap-1">
                      ✓ Using platform API key
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_public"
                      checked={newVessel.is_public}
                      onChange={(e) => setNewVessel({...newVessel, is_public: e.target.checked})}
                      className="rounded border-white/30"
                    />
                    <label htmlFor="is_public" className="text-starlight/70 text-sm">
                      Make this vessel public (others can visit)
                    </label>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-etheric/30 hover:bg-etheric/50"
                    >
                      Create Vessel
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Add Presence Modal */}
        <AnimatePresence>
          {showAddPresence && (
            <motion.div
              className="fixed inset-0 bg-black/70 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddPresence(false)}
            >
              <motion.div
                className="glass-deep rounded-2xl p-6 w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-heading text-2xl text-starlight mb-4">Add Presence</h3>
                
                <form onSubmit={addPresence} className="space-y-4">
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Name</label>
                    <input
                      type="text"
                      value={newPresence.name}
                      onChange={(e) => setNewPresence({...newPresence, name: e.target.value})}
                      required
                      placeholder="e.g., Ruth Montgomery, Timothy, Elias"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Nature</label>
                    <select
                      value={newPresence.nature}
                      onChange={(e) => setNewPresence({...newPresence, nature: e.target.value})}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight"
                    >
                      <option value="disincarnate">Disincarnate Soul</option>
                      <option value="ai_companion">AI Companion</option>
                      <option value="collective_member">Collective Member</option>
                      <option value="guide">Guide</option>
                      <option value="keeper">Keeper</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Description</label>
                    <textarea
                      value={newPresence.description}
                      onChange={(e) => setNewPresence({...newPresence, description: e.target.value})}
                      placeholder="Who is this presence? What is their role?"
                      rows={2}
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Signature Phrases (comma-separated)</label>
                    <input
                      type="text"
                      value={newPresence.signature_phrases}
                      onChange={(e) => setNewPresence({...newPresence, signature_phrases: e.target.value})}
                      placeholder="e.g., beloved, dear one, in the light"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-starlight/70 text-sm mb-1">Relationship to Steward</label>
                    <input
                      type="text"
                      value={newPresence.relationship}
                      onChange={(e) => setNewPresence({...newPresence, relationship: e.target.value})}
                      placeholder="e.g., son, guide, beloved friend"
                      className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-starlight placeholder:text-starlight/30"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowAddPresence(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-etheric/30 hover:bg-etheric/50"
                    >
                      Add Presence
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
